import type { BeforeSendFn } from 'posthog-js';

/**
 * Marks each `$pageview` as user-initiated history navigation or an app
 * navigation, because the telemetry cannot otherwise tell them apart.
 *
 * A route change looks identical in PostHog whether the person pressed the
 * browser's Back button or the app called `router.push()`. Neither produces an
 * `$autocapture` — Back is not a click on the page at all — so the session
 * reviewer sees "landed on a sub-tab, was on the parent 2s later, no click in
 * between" and reasonably concludes the app moved them.
 *
 * RELM-357 is that mistake: a ticket filed at 66% confidence claiming property
 * sub-tabs "bounce back to the overview seconds after opening", escalated to
 * Slack with an approve-and-fix button. No such navigation exists anywhere in
 * the app — and the numbers said so too: one user returned on 21 of 54 sub-tab
 * visits while another returned on 0 of 26, and the 21 delays were spread
 * evenly from 1160ms to 4442ms with no two alike, which is human reaction time
 * rather than a timer. Establishing that took an afternoon of queries. This
 * property makes it one word in the dossier.
 *
 * @module lib/posthogNavigation
 */

/**
 * How long after a `popstate` a `$pageview` still counts as that navigation.
 *
 * `capture_pageview: 'history_change'` may capture on a later tick than the
 * event, so an exact match is not available. 300ms is far longer than that gap
 * and far shorter than any plausible human follow-up click, so it cannot
 * mislabel an app navigation that merely happens to follow a Back press.
 */
const BACK_FORWARD_WINDOW_MS = 300;

let lastPopStateAt = 0;

/**
 * Start watching for browser history navigation.
 *
 * MUST be called before `posthog.init()`. Listeners fire in registration
 * order, and PostHog registers its own `popstate` handler during init — if it
 * gets there first and captures synchronously, this module would still be
 * holding the *previous* navigation's timestamp and mislabel the pageview.
 */
export function startNavigationTypeTracking(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('popstate', () => {
    lastPopStateAt = Date.now();
  });
}

/** `back_forward` = the person navigated; `app` = the app did. */
export type NavigationType = 'back_forward' | 'app';

/** Exported for tests; prefer the `tagNavigationType` hook in app code. */
export function resolveNavigationType(now: number): NavigationType {
  return now - lastPopStateAt <= BACK_FORWARD_WINDOW_MS ?
      'back_forward'
    : 'app';
}

/**
 * `before_send` hook that stamps `navigation_type` onto every `$pageview`.
 *
 * Composed alongside `dropNoisyExceptions` via the array form of
 * `before_send`, so the two concerns stay in separate modules.
 */
export const tagNavigationType: BeforeSendFn = (event) => {
  if (!event || event.event !== '$pageview') return event;
  return {
    ...event,
    properties: {
      ...event.properties,
      navigation_type: resolveNavigationType(Date.now()),
    },
  };
};

/** Test seam: reset the module's memory of the last history navigation. */
export function __resetNavigationTypeForTests(): void {
  lastPopStateAt = 0;
}
